/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Grid, Typography, Button, Paper } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import { getSessionState } from '../../actions/getSessionState';
import { beginSession } from '../../actions/beginSession';
import { useStyles } from './AssessmentSessions.style';

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const formatRemaining = (seconds) => {
  const safe = Math.max(0, Number(seconds) || 0);
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
};

// "Session X of Y" control. The Assessment app is the single source of truth for
// attempt/sitting state (spec §2.3); this reads it via /candidate/:id/session-state
// and books the next sitting via /user/session. The gate mirrors the server guard:
// a next sitting can be booked only when the attempt is paused with sittings and
// time remaining (status — not gate_status — is the discriminator).
const AssessmentSessions = (props) => {
  const classes = useStyles();
  const { candidateId, sessionState, doneSelfAssessment } = props;

  useEffect(() => {
    if (candidateId) {
      props.getSessionState(candidateId);
    }
  }, [candidateId]);

  if (!candidateId || !sessionState) return null;

  const { status, gateStatus, sittingNumber, remainingSeconds, maxSittings } =
    sessionState;

  // Fully finished → nothing here.
  if (status === 'completed') return null;

  // No attempt yet (sitting 1 is auto-booked elsewhere). Greet a candidate who has
  // finished self-assessment and is awaiting their first start; the pre-self-assessment
  // state is handled by PendingAssessments. Sponsors never reach here (candidateId is
  // null for them, so the guard above returns null).
  if (status == null) {
    if (!doneSelfAssessment) return null;
    return (
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5">Your Assessment</Typography>
          </Grid>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Grid container spacing={3} wrap="nowrap" alignItems="flex-start">
                <Grid item>
                  <div className={classes.iconContainer}>
                    <CheckCircleIcon className={classes.icon} />
                  </div>
                </Grid>
                <Grid item xs>
                  <Typography
                    variant="h4"
                    color="textPrimary"
                    gutterBottom
                    className={classes.headline}
                  >
                    You&apos;re all set — your assessment is booked and ready to
                    begin.
                  </Typography>
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    gutterBottom
                    className={classes.body}
                  >
                    Please check your email for a secure invite from our
                    proctoring partner (ProctorExam) and follow the link there to
                    begin. You can complete the assessment in up to 3 sittings over
                    a total of 3 hours.
                  </Typography>
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    gutterBottom
                    className={classes.body}
                  >
                    To pause and resume later, use &apos;End session&apos;. Logging
                    out does not pause your timer — it is intended for comfort
                    breaks or short periods away from your computer.
                  </Typography>
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    className={classes.body}
                  >
                    Can&apos;t find the email? Please check your spam folder, or
                    contact{' '}
                    <a href="mailto:contact@competas.com">contact@competas.com</a>{' '}
                    and we&apos;ll help you get started.
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  const totalSittings = maxSittings || 3;
  const canBegin =
    status === 'paused' && sittingNumber < totalSittings && remainingSeconds > 0;
  const awaitingProctor =
    status === 'in_progress' && gateStatus === 'awaiting_proctor';

  const handleBegin = () => props.beginSession({ candidateId });

  return (
    <Grid item xs={12}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5">Your Assessment</Typography>
        </Grid>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Grid container spacing={3} wrap="nowrap" alignItems="flex-start">
              <Grid item>
                <div className={classes.iconContainer}>
                  <CheckCircleIcon className={classes.icon} />
                </div>
              </Grid>
              <Grid item xs>
                <Typography
                  variant="h4"
                  color="textPrimary"
                  gutterBottom
                  className={classes.headline}
                >
                  Session {sittingNumber} of {totalSittings}
                </Typography>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  gutterBottom
                  className={classes.body}
                >
                  Remaining time: {formatRemaining(remainingSeconds)}.
                </Typography>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  gutterBottom
                  className={classes.body}
                >
                  You may complete this assessment in up to {totalSittings}{' '}
                  sittings over a total of 3 hours. To pause and resume later, use
                  &apos;End session&apos; — logging out does not pause your timer.
                </Typography>
                {awaitingProctor && (
                  <Alert severity="info">
                    <Typography variant="subtitle2">
                      <strong>Next session booked</strong>
                    </Typography>
                    Check your email for a new secure invite from our proctoring
                    partner (ProctorExam) to begin your next session.
                  </Alert>
                )}
                {canBegin && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleBegin}
                  >
                    Begin next session
                  </Button>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  );
};

AssessmentSessions.propTypes = {
  candidateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sessionState: PropTypes.object,
  doneSelfAssessment: PropTypes.bool,
  getSessionState: PropTypes.func.isRequired,
  beginSession: PropTypes.func.isRequired,
};

AssessmentSessions.defaultProps = {
  candidateId: null,
  sessionState: null,
  doneSelfAssessment: false,
};

const mapStateToProps = ({ candidatePortal }) => {
  const { sessionState } = candidatePortal;
  return { sessionState };
};

export default connect(mapStateToProps, { getSessionState, beginSession })(
  AssessmentSessions,
);
